import React, { FC, useCallback, useEffect, useState } from 'react'
import DocumentTitle from 'react-document-title'
import { useHistory, useParams } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import XArrow from 'react-xarrows'
import { IconButton } from 'transfix-ui/components/IconButton'
import OverflowMenu, { useOverflowToggle } from 'transfix-ui/components/OverflowMenu'
import { Switch } from 'transfix-ui/components/Switch'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box, Button, makeStyles, Step, StepLabel, Stepper, Typography } from 'transfix-ui/core'
import { DocumentIcon, SettingsIcon } from 'transfix-icons'
import { useModal } from 'utils/hooks'
import { EDI_TOOLS_ROUTES } from '../LandingPage'
import { CodeEditor } from './Editor/CodeEditor'
import { InputSidePanel } from './InputSidePanel'
import { MakeLiveConfirmationModal } from './MakeLiveConfirmationModal'
import { ParserOptionsModal } from './ParserOptions/ParserOptionsModal'
import { ParserTestsModal } from './ParserTests/ParserTestsModal'
import { SaveParserTestModal } from './ParserTests/SaveParserTestModal'
import { ParserValidationErrorsModal } from './ParserValidationErrorsModal'
import { ParserVersionConfirmationModal } from './ParserVersions/ParserVersionConfirmationModal'
import { ParserVersionModal } from './ParserVersions/ParserVersionModal'
import {
  getParserConfigs,
  getParsers,
  getParserTests,
  getParserVersions,
  runParserVersion,
  updateParserVersion,
} from './api'
import {
  Parser,
  parserVersionRequest,
  runParserRequest,
  parserResponse,
  ParserTest,
  ParserVersion,
  STEP_NAMES,
  tfx12Config,
} from './types'
import {
  formatConversionInstructions,
  formatParsers,
  formatParserVersions,
  hasConversionErrors,
  hasExtractionErrors,
} from './utils'

interface IEdiSelfServiceProps {}

const useStyles = makeStyles(theme => ({
  container: {},
  row: {
    width: '100%',
    marginBottom: '1em',
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'left',
  },
  editor: {
    borderRight: 'solid 1px',
  },
  topNav: {
    marginBottom: theme.spacing(1),
    height: '50%',
  },
  transformButton: {
    margin: theme.spacing(2),
    marginLeft: theme.spacing(4),
  },
  sidePanelButton: {
    maxWidth: '150px',
    maxHeight: '60px',
    minWidth: '100px',
    minHeight: '60px',
  },
  tooltip: {
    maxWidth: '150px',
  },
  editButton: {
    marginBottom: theme.spacing(2),
  },
  parserVersionsButton: {
    marginLeft: theme.spacing(1),
  },
  stepper: {
    marginLeft: '175px',
    height: '105px',
  },
  modalButtons: {
    float: 'right',
    marginTop: theme.spacing(3),
  },
  column: {
    marginRight: theme.spacing(4),
  },
  options: {
    marginRight: theme.spacing(4),
    marginTop: theme.spacing(8),
  },
  topNavButtons: {
    marginLeft: '25px',
  },
}))

export type ValidationStatusModal = 'validationStatusModal'
export type ParserOptionsModal = 'parserOptionsModal'
export type ParserVersionsModal = 'parserVersionsModal'
export type MakeLiveConfirmationModal = 'makeLiveConfirmationModal'
export type SaveParserTestModal = 'saveParserTestModal'
export type ManageParserTestsModal = 'manageParserTestsModal'
export type ParserVersionConfirmationModal = 'parserVersionConfirmationModal'

export const EdiSelfService: FC<IEdiSelfServiceProps> = () => {
  const history = useHistory()
  const { parserVersionId } = useParams<{ parserVersionId: string }>()
  const { parserTestId } = useParams<{ parserTestId: string }>()
  const [rawEdiContent, setRawEdiContent] = useState<string>()
  const [ediConvertedToJson, setEdiConvertedToJson] = useState<string>()
  const [extractedJson, setExtractedJson] = useState<string>()
  const [finalJSONOutput, setFinalJSONOutput] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [jsonataPanelOpen, setJsonataPanelOpen] = useState<boolean>(false)
  const [conversionInstructionsPanelOpen, setConversionInstructionsPanelOpen] = useState<boolean>(
    false
  )
  const [extractionErrors, setExtractionErrors] = useState<string[] | null>(null)
  const [conversionErrors, setConversionErrors] = useState<string[] | null>(null)
  const [debugLogs, setDebugLogs] = useState<string[][] | null>()
  const [showExtractArrow, setShowExtractArrow] = useState<boolean>(false)
  const [helpStepsEnabled, setHelpStepsEnabled] = useState<boolean>(true)
  const [isValidSchema, setIsValidSchema] = useState<boolean>(false)
  const [schemaValidationErrors, setSchemaValidationErrors] = useState<[string[]]>()
  const [activeStep, setActiveStep] = useState<number>(0)
  const [stupidediConfigOptions, setStupidediConfigOptions] = useState<string[]>([])
  const [tfx12ConfigOptions, setTfx12ConfigOptions] = useState<tfx12Config[]>([])
  const [documentConversionOptions, setDocumentConversionOptions] = useState<string[]>([])
  const [elementConversionOptions, setElementConversionOptions] = useState<string[]>([])
  const [allParsers, setAllParsers] = useState<Parser[]>([])
  const [currentParserVersion, setCurrentParserVersion] = useState<ParserVersion>()
  const [parserTests, setParserTests] = useState<ParserTest[]>([])

  const toggleHelp = () => setHelpStepsEnabled(!helpStepsEnabled)

  const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()

  const { closeModal, isOpen, openModal } = useModal<ValidationStatusModal>()

  const {
    closeModal: closeParserVersionConfirmationModal,
    isOpen: isParserVersionConfirmationModalOpen,
    openModal: openParserVersionConfirmationModal,
  } = useModal<ParserVersionConfirmationModal>()

  const {
    closeModal: closeParserOptionsModal,
    isOpen: isParserOptionsModalOpen,
    openModal: openParserOptionsModal,
  } = useModal<ParserOptionsModal>()

  const {
    closeModal: closeParserVersionsModal,
    isOpen: isParserVersionsModalOpen,
    openModal: openParserVersionsModal,
  } = useModal<ParserVersionsModal>()

  const {
    closeModal: closeMakeLiveConfirmationModal,
    isOpen: isMakeLiveConfirmationModal,
    openModal: openMakeLiveConfirmationModal,
  } = useModal<MakeLiveConfirmationModal>()

  const {
    closeModal: closeSaveParserTestModal,
    isOpen: isSaveParserTestModalOpen,
    openModal: openSaveParserTestModal,
  } = useModal<SaveParserTestModal>()

  const {
    closeModal: closeManageParserTestsModal,
    isOpen: isManageParserTestModalOpen,
    openModal: openManageParserTestsModal,
  } = useModal<ManageParserTestsModal>()

  const { closeToast, multiLine, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const openValidationStatusModal = openModal('validationStatusModal')

  const openOptionsModal = openParserOptionsModal('parserOptionsModal')

  const openVersionsModal = openParserVersionsModal('parserVersionsModal')

  const openMakeLiveModal = openMakeLiveConfirmationModal('makeLiveConfirmationModal')

  const openParserTestModal = openSaveParserTestModal('saveParserTestModal')

  const openParserTestsModal = openManageParserTestsModal('manageParserTestsModal')

  const openVersionConfirmationModal = openParserVersionConfirmationModal(
    'parserVersionConfirmationModal'
  )

  const fetchParserTests = async () => {
    const parserTests: ParserTest[] = await getParserTests()
    setParserTests(parserTests)
  }

  const validationStatusHandler = () => {
    openValidationStatusModal()
  }

  const saveParserTestHandler = async () => {
    fetchParserTests()
    closeSaveParserTestModal()
  }

  const documentationHandler = () => {
    window.open(`${__ROOT__}/edi-tools/inbound-parser-documentation`, '_blank')
  }

  const resetUI = () => {
    setFinalJSONOutput(undefined)
    setRawEdiContent(undefined)
    setEdiConvertedToJson(undefined)
    setExtractedJson(undefined)
    setExtractionErrors(null)
    setConversionErrors(null)
  }

  const parserVersionChangeHandler = (parserVersion: ParserVersion) => {
    history.push(`${EDI_TOOLS_ROUTES.SELF_SERVE_INBOUND}/parser-version/${parserVersion.id}`)
  }

  const loadParserVersion = useCallback(
    (parserVersion: ParserVersion) => {
      setCurrentParserVersion(parserVersion)
      resetUI()
      setActiveStep(1)
      closeParserVersionsModal()
    },
    [closeParserVersionsModal]
  )

  const saveParserVersion = async () => {
    const body: parserVersionRequest = {
      configurable_parser_version: {
        is_live: currentParserVersion?.is_live,
        parser_library: currentParserVersion?.parser_library,
        stupidedi_config: currentParserVersion?.stupidedi_config,
        edi_mapper_tfx12_definition_id: currentParserVersion?.edi_mapper_tfx12_definition_id,
        extraction_template: currentParserVersion?.extraction_template,
        conversion_instructions: currentParserVersion?.conversion_instructions,
        element_conversion: currentParserVersion?.element_conversion,
        document_conversion: currentParserVersion?.document_conversion,
        notes: currentParserVersion?.notes,
      },
    }
    try {
      await updateParserVersion(body, currentParserVersion)
      openToast({
        toastType: 'default',
        toastMessage: `Successfully saved changes to this parser`,
      })
    } catch (e) {
      openToast({
        toastType: 'alert',
        toastMessage: `An error occured saving changes to this parser: ${e}`,
        multiLine: e.length > 300,
      })
    }
  }

  const fetchParsers = async () => {
    const parsers = await getParsers()
    const parserVersions = await getParserVersions()
    const formattedParserVersions = formatParserVersions(parsers, parserVersions)
    const mappedParsers = formatParsers(parsers, formattedParserVersions)
    setAllParsers(mappedParsers)
  }

  const fetchParserVersion = useCallback(() => {
    const parserVersion = allParsers
      .flatMap(parser => parser.parserVersions)
      .find(pv => pv.id === parseInt(parserVersionId, 10))
    if (parserVersion) {
      loadParserVersion(parserVersion)
    }
  }, [allParsers, loadParserVersion, parserVersionId])

  const onMakeLiveSuccess = () => {
    if (currentParserVersion) {
      currentParserVersion.is_live = true
    }
    openToast({
      toastType: 'default',
      toastMessage: `This parser is now live!`,
    })
    fetchParsers()
  }

  const displayError = (error: string) => {
    openToast({
      toastType: 'alert',
      toastMessage: `${error}`,
    })
  }

  const initializeParserConfigOptions = async () => {
    const configs = await getParserConfigs()
    setStupidediConfigOptions(configs.stupidediConfigs)
    setTfx12ConfigOptions(configs.tfx12Configs)
    setDocumentConversionOptions(configs.documentConversions)
    setElementConversionOptions(configs.elementConversions)
  }

  const stupidediConfigChangeHandler = (stupidediConfig: string) => {
    if (currentParserVersion) {
      currentParserVersion.stupidedi_config = stupidediConfig
    }
  }

  const tfx12ConfigReferenceChangeHandler = (tfx12ConfigReference: string) => {
    if (currentParserVersion && tfx12ConfigOptions) {
      const tfx12Config = tfx12ConfigOptions.find(
        config => config.reference === tfx12ConfigReference
      )
      currentParserVersion.edi_mapper_tfx12_definition_id = tfx12Config?.id
    }
  }

  const parserLibraryChangeHandler = (parserLibrary: string) => {
    if (currentParserVersion) {
      currentParserVersion.parser_library = parserLibrary
    }
  }

  const documentConversionChangeHandler = (docuentConversionOption: string) => {
    if (currentParserVersion) {
      currentParserVersion.document_conversion = docuentConversionOption
    }
  }

  const elementConversionChangeHandler = (elementConversionOption: string) => {
    if (currentParserVersion) {
      currentParserVersion.element_conversion = elementConversionOption
    }
  }

  const overflowOptions = [
    {
      itemComponent: (
        <Box display='flex' alignItems='items-center'>
          <Typography variant='button' component='div'>
            Make Live
          </Typography>
        </Box>
      ),
      label: 'Make Live',
      action: openMakeLiveModal,
    },
    {
      itemComponent: (
        <Box display='flex' alignItems='items-center'>
          <Typography variant='button' component='div'>
            Save Version
          </Typography>
        </Box>
      ),
      label: 'Parser Options',
      action: () => {
        saveParserVersion()
      },
    },
    {
      itemComponent: (
        <Box display='flex' alignItems='items-center'>
          <Typography variant='button' component='div'>
            Save As Test
          </Typography>
        </Box>
      ),
      label: 'Save As Test',
      action: openParserTestModal,
    },
    {
      itemComponent: (
        <Box display='flex' alignItems='items-center'>
          <Typography variant='button' component='div'>
            Version Notes
          </Typography>
        </Box>
      ),
      label: 'Version Notes',
      action: openVersionConfirmationModal,
    },
    {
      itemComponent: (
        <Box display='flex' alignItems='items-center'>
          <Typography variant='button' component='div'>
            Manage Tests
          </Typography>
        </Box>
      ),
      label: 'Manage Tests',
      action: openParserTestsModal,
    },
    {
      itemComponent: (
        <Box display='flex' alignItems='items-center'>
          <Typography variant='button' component='div'>
            Documentation
          </Typography>
        </Box>
      ),
      label: 'Parser Documentation',
      action: documentationHandler,
    },
  ]

  const transformationHandler = (response: parserResponse) => {
    setActiveStep(2)
    setEdiConvertedToJson(JSON.stringify(response.transformed_edi, null, 2))
    setShowExtractArrow(true)
  }

  const extractionHandler = (response: parserResponse) => {
    if (response.extracted_data === null) {
      if (extractedJson !== undefined) {
        setExtractedJson(JSON.stringify('No data was extracted using the provided jsonata query'))
      }
    } else if (
      currentParserVersion?.extraction_template &&
      currentParserVersion?.extraction_template.length > 0
    ) {
      setActiveStep(3)
      setExtractedJson(JSON.stringify(response.extracted_data, null, 2))
    }

    if (hasExtractionErrors(response)) {
      setExtractionErrors([response.extraction_errors])
    }
  }

  const conversionHandler = (response: parserResponse) => {
    if (response.valid) {
      setActiveStep(5)
    } else {
      setActiveStep(4)
    }
    if (hasConversionErrors(response)) {
      setConversionErrors(response.conversion_errors)
    }
    setFinalJSONOutput(JSON.stringify(response.final_output, null, 2))
    setIsValidSchema(response.valid)
    if (response.schema_validation_errors) {
      setSchemaValidationErrors(response.schema_validation_errors)
    }
    setDebugLogs(response.conversion_debug_logs || null)
  }

  const jsonataChangeHandler = (jsonataQuery: string) => {
    if (currentParserVersion && String(currentParserVersion?.id) === parserVersionId) {
      currentParserVersion.extraction_template = jsonataQuery
    }
  }

  const conversionDataChangeHandler = (conversionData: string) => {
    try {
      if (currentParserVersion && String(currentParserVersion?.id) === parserVersionId) {
        currentParserVersion.conversion_instructions = JSON.parse(conversionData)
      }
    } catch (e) {
      return false
    }
    return true
  }

  const versionConfirmationHandler = async () => {
    await fetchParsers()
    closeParserVersionConfirmationModal()
  }

  const parserTestChangeHandler = (parserTest: ParserTest) => {
    history.push(
      `${EDI_TOOLS_ROUTES.SELF_SERVE_INBOUND}/parser-version/${currentParserVersion?.id}/parser-test/${parserTest.id}`
    )
  }

  const loadTestHandler = useCallback(
    (parserTest: ParserTest) => {
      setEdiConvertedToJson(undefined)
      setRawEdiContent(parserTest.edi_input)
      setFinalJSONOutput(JSON.stringify(parserTest.expected_output))
      closeManageParserTestsModal()
    },
    [closeManageParserTestsModal]
  )

  const fetchParserTest = useCallback(() => {
    const parserTest = parserTests.find(
      pt =>
        pt.id === parseInt(parserTestId, 10) &&
        pt.edi_mapper_configurable_parser_id === currentParserVersion?.parent_parser?.id
    )
    if (parserTest) {
      loadTestHandler(parserTest)
    }
  }, [currentParserVersion?.parent_parser?.id, loadTestHandler, parserTestId, parserTests])

  useEffectOnce(() => {
    try {
      initializeParserConfigOptions()
      fetchParsers()
      fetchParserTests()
    } catch (e) {
      displayError(e)
    }
  })

  useEffect(() => {
    fetchParserVersion()
    if (parserTestId) {
      fetchParserTest()
    }
  }, [fetchParserVersion, fetchParserTest, parserTestId, parserVersionId])

  const runParser = async (step: STEP_NAMES) => {
    setLoading(true)
    setExtractionErrors(null)
    setConversionErrors(null)

    try {
      const body: runParserRequest = {
        rawEdi: rawEdiContent,
        extractionTemplate: currentParserVersion?.extraction_template,
        conversionInstructions: JSON.stringify(currentParserVersion?.conversion_instructions),
        parserLibrary: currentParserVersion?.parser_library,
        stupidediConfig: currentParserVersion?.stupidedi_config,
        ediMapperTfx12DefinitionId: currentParserVersion?.edi_mapper_tfx12_definition_id,
        elementConversion: currentParserVersion?.element_conversion || 'default',
        documentConversion: currentParserVersion?.document_conversion || 'default',
      }

      const response: parserResponse = await runParserVersion(body)

      if (step === STEP_NAMES.TRANSFORMATION) {
        transformationHandler(response)
      } else if (step === STEP_NAMES.EXTRACTION) {
        extractionHandler(response)
      } else if (step === STEP_NAMES.CONVERSION) {
        conversionHandler(response)
      }
      setLoading(false)
    } catch (e) {
      openToast({
        toastType: 'alert',
        toastMessage: `An error occured: ${e}`,
        multiLine: e.length > 300,
      })
      setLoading(false)
    }
  }

  const classes = useStyles()

  const steps = [
    'Load Parser',
    'Transform EDI into JSON',
    'Extract the transformed JSON',
    'Specify conversion instructions',
    'Valid Schema?',
    'Save Version',
  ]

  const rawEdiChangeHandler = React.useCallback(value => {
    setRawEdiContent(value)
  }, [])

  const openJsonataOverlay = () => {
    setJsonataPanelOpen(true)
  }

  const openConversionOverlay = () => {
    setConversionInstructionsPanelOpen(true)
  }

  const isStepFailed = (step: number) => {
    switch (step) {
      case 0:
        return false
      case 1:
        return extractionErrors !== undefined && extractionErrors !== null
      case 2:
        return conversionErrors !== undefined && conversionErrors !== null
      case 3:
        return (
          schemaValidationErrors !== undefined &&
          schemaValidationErrors !== null &&
          schemaValidationErrors.join('').length > 0 &&
          schemaValidationErrors.length > 0
        )
      default:
        return false
    }
  }

  const getErrorMessageForStep = (step: number) => {
    switch (step) {
      case 1:
        return extractionErrors?.join('')
      case 2:
        return conversionErrors?.join('')
      case 3:
        return 'Your schema is not valid and has errors'
      default:
        return null
    }
  }

  const getInstructionalMessageForStep = (step: number) => {
    switch (step) {
      case 0:
        return 'The first step in the process is to paste in your EDI and have it transformed into JSON'
      case 1:
        return 'The second step in the process is to extract out some JSON using a JSONata query.'
      case 2:
        return 'The third step in the process is to specify conversion instructions.  These will map json values to method names'
      case 3:
        return isValidSchema
          ? 'Your schema is valid!'
          : 'Finally, ensure the schema is valid and pass the tests.'
      default:
        return null
    }
  }

  return (
    <DocumentTitle title='EDI Self Service' data-test='edi-self-service'>
      <Box component='div' className={classes.container}>
        <TopNav
          className={classes.topNav}
          title={
            <Box display='flex' flexDirection='row' alignItems='center' flexWrap='wrap'>
              <Typography variant='h3'>EDI Self Service Parser</Typography>
              <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
                {steps.map((label, index) => {
                  let errorMessage = ''
                  const informationalMessage = getInstructionalMessageForStep(index)
                  const labelProps: {
                    optional?: React.ReactNode
                    error?: boolean
                  } = {}
                  if (isStepFailed(index)) {
                    labelProps.error = true
                    errorMessage = getErrorMessageForStep(index) || ''
                  }
                  return (
                    <Step key={label}>
                      <StepLabel {...labelProps}>
                        <Tooltip
                          position='bottom'
                          id={`${index}-step`}
                          type='dark'
                          trigger={<Typography variant='caption'>{label}</Typography>}>
                          <Box className={classes.tooltip}>
                            {labelProps.error ? errorMessage : informationalMessage}
                          </Box>
                        </Tooltip>
                      </StepLabel>
                    </Step>
                  )
                })}
              </Stepper>
              <Box className={classes.topNavButtons}>
                <Switch label='Show help arrows' onChange={toggleHelp} checked={helpStepsEnabled} />
              </Box>
              <Button onClick={openOptionsModal}>Parser Options</Button>
              <Button className={classes.parserVersionsButton} onClick={openVersionsModal}>
                Load Parser
              </Button>
              <OverflowMenu
                options={overflowOptions}
                trigger={
                  <IconButton
                    color='tertiary'
                    label='overflow-trigger'
                    Icon={SettingsIcon}
                    onClick={clickOverflowTrigger}
                    data-test='overflow-menu'
                    size='large'
                  />
                }
                expanded={expanded}
                onExpanded={setExpanded}
                dropdownId='test'
              />
              <IconButton
                Icon={DocumentIcon}
                color='tertiary'
                size='large'
                onClick={documentationHandler}
                data-test='edit-accounting'
                label='Open Documentation'
              />
              {currentParserVersion && (
                <Typography variant='caption'>
                  {' '}
                  Currently Editing {currentParserVersion.parent_parser?.name} Parser Version ID{' '}
                  {currentParserVersion.id}
                  {currentParserVersion.is_live ? '(Live, cannot be modified)' : '(Not Live)'}
                </Typography>
              )}
            </Box>
          }
        />
        <Box component='div' className={classes.row}>
          <Box className={classes.column} ml={2}>
            <Typography>Paste in your EDI below to begin!</Typography>
            <CodeEditor
              height='30vh'
              width='40vw'
              value={rawEdiContent}
              onInputDataChange={rawEdiChangeHandler}
              language='plaintext'
            />
            <Button
              id='transformButton'
              onClick={() => {
                runParser(STEP_NAMES.TRANSFORMATION)
              }}
              className={classes.transformButton}
              disabled={rawEdiContent === undefined || rawEdiContent.length === 0 || loading}>
              Transform into JSON
            </Button>
            <Box id='transformedJSONMirror'>
              <CodeEditor
                height='55vh'
                width='40vw'
                value={
                  loading && ediConvertedToJson === undefined ? 'Loading ...' : ediConvertedToJson
                }
                language='json'
              />
            </Box>
          </Box>
          <Box className={classes.options}>
            <Box mb={9}>
              <Button
                id='queryButton'
                size='medium'
                onClick={openJsonataOverlay}
                className={classes.sidePanelButton}
                style={{
                  maxWidth: '150px',
                  maxHeight: '60px',
                  minWidth: '150px',
                  minHeight: '60px',
                }}>
                Extract Data
              </Button>
            </Box>
            <Box mb={9}>
              <Button
                className={classes.sidePanelButton}
                id='conversionButton'
                size='medium'
                onClick={openConversionOverlay}
                style={{
                  maxWidth: '150px',
                  maxHeight: '60px',
                  minWidth: '150px',
                  minHeight: '60px',
                }}>
                Conversion Instructions
              </Button>
            </Box>
            <Box mb={9}>
              <Button
                id='validateButton'
                size='medium'
                onClick={validationStatusHandler}
                className={classes.sidePanelButton}
                style={{
                  maxWidth: '150px',
                  maxHeight: '60px',
                  minWidth: '150px',
                  minHeight: '60px',
                }}
                disabled={
                  finalJSONOutput === undefined ||
                  schemaValidationErrors === undefined ||
                  schemaValidationErrors.join('').length === 0
                }>
                Schema Errors
              </Button>
            </Box>
            <Box>
              <Button
                id='validateButton'
                size='medium'
                onClick={saveParserVersion}
                className={classes.sidePanelButton}
                disabled={currentParserVersion?.is_live || currentParserVersion?.was_ever_live}
                style={{
                  maxWidth: '150px',
                  maxHeight: '60px',
                  minWidth: '150px',
                  minHeight: '60px',
                }}>
                Save Version
              </Button>
            </Box>
          </Box>
          <Box className={classes.column} ml={2} id='finalOutputMirror'>
            <Typography>Final Output</Typography>
            <CodeEditor height='80vh' width='45vw' value={finalJSONOutput} language='json' />
          </Box>
        </Box>
        <InputSidePanel
          title='Extract Data'
          type='extraction'
          onClose={() => {
            setJsonataPanelOpen(false)
          }}
          sidebarOpen={jsonataPanelOpen}
          inputData={currentParserVersion?.extraction_template || ''}
          onInputDataChange={jsonataChangeHandler}
          onSubmit={() => {
            runParser(STEP_NAMES.EXTRACTION)
          }}
          outputData={extractedJson}
          loading={loading}
          outputErrors={extractionErrors}
          referenceData={ediConvertedToJson}
          onTransitionToNextStep={() => {
            setJsonataPanelOpen(false)
            setConversionInstructionsPanelOpen(true)
          }}
          currentParserVersion={currentParserVersion}
        />
        <InputSidePanel
          title='Conversion Instructions'
          type='conversion'
          onClose={() => {
            setConversionInstructionsPanelOpen(false)
          }}
          sidebarOpen={conversionInstructionsPanelOpen}
          inputData={
            formatConversionInstructions(
              JSON.stringify(currentParserVersion?.conversion_instructions, null, 2)
            ) || ''
          }
          onInputDataChange={conversionDataChangeHandler}
          onSubmit={() => {
            runParser(STEP_NAMES.CONVERSION)
          }}
          outputData={finalJSONOutput}
          referenceData={extractedJson}
          loading={loading}
          outputErrors={conversionErrors}
          debugLogs={debugLogs}
          onTransitionToNextStep={() => {}}
          currentParserVersion={currentParserVersion}
        />
        {isOpen('validationStatusModal') && (
          <ParserValidationErrorsModal
            schemaErrors={schemaValidationErrors}
            onCancel={closeModal}
            onClose={closeModal}
          />
        )}
        {isParserOptionsModalOpen('parserOptionsModal') && (
          <ParserOptionsModal
            onClose={closeParserOptionsModal}
            stupidediConfigOptions={stupidediConfigOptions}
            tfx12ConfigOptions={tfx12ConfigOptions}
            documentConversionOptions={documentConversionOptions}
            elementConversionOptions={elementConversionOptions}
            currentParserVersion={currentParserVersion}
            onStupidediConfigChange={stupidediConfigChangeHandler}
            onTfx12ConfigReferenceChange={tfx12ConfigReferenceChangeHandler}
            onParserLibraryChange={parserLibraryChangeHandler}
            onDocumentConversionOptionChange={documentConversionChangeHandler}
            onElementConversionOptionChange={elementConversionChangeHandler}
          />
        )}
        {isParserVersionConfirmationModalOpen('parserVersionConfirmationModal') && (
          <ParserVersionConfirmationModal
            onClose={closeParserVersionConfirmationModal}
            onSuccess={versionConfirmationHandler}
            selectedParserVersion={currentParserVersion}
            setSelectedParserVersion={setCurrentParserVersion}
            type='edit'
          />
        )}
        {(isParserVersionsModalOpen('parserVersionsModal') ||
          currentParserVersion === undefined) && (
          <ParserVersionModal
            parsers={allParsers}
            onParserVersionSelect={parserVersionChangeHandler}
            onClose={closeParserVersionsModal}
            onSuccess={fetchParsers}
          />
        )}
        {isManageParserTestModalOpen('manageParserTestsModal') && (
          <ParserTestsModal
            parserTests={parserTests.filter(
              parserTest =>
                parserTest.edi_mapper_configurable_parser_id ===
                currentParserVersion?.parent_parser?.id
            )}
            onLoadTest={parserTestChangeHandler}
            onClose={closeManageParserTestsModal}
            parserVersion={currentParserVersion}
            onSuccess={fetchParserTests}
            ediInput={rawEdiContent}
            expectedOutput={finalJSONOutput}
          />
        )}
        {isSaveParserTestModalOpen('saveParserTestModal') && (
          <SaveParserTestModal
            onClose={closeSaveParserTestModal}
            selectedParserVersion={currentParserVersion}
            ediInput={rawEdiContent || ''}
            expectedOutput={finalJSONOutput || ''}
            onSuccess={saveParserTestHandler}
            isLive
          />
        )}
        {isMakeLiveConfirmationModal('makeLiveConfirmationModal') && (
          <MakeLiveConfirmationModal
            onClose={closeMakeLiveConfirmationModal}
            onSuccess={onMakeLiveSuccess}
            onError={error => displayError(error)}
            currentParserVersion={currentParserVersion}
          />
        )}
        {helpStepsEnabled && showExtractArrow && (
          <XArrow
            start='transformedJSONMirror'
            end='queryButton'
            startAnchor='top'
            path='straight'
          />
        )}
        {helpStepsEnabled && extractedJson !== undefined && extractionErrors === null && (
          <XArrow
            start='queryButton'
            end='conversionButton'
            startAnchor='auto'
            endAnchor='auto'
            path='straight'
          />
        )}
        <Toast
          variation={toastType}
          open={toastOpen}
          onClose={closeToast}
          message={toastMessage}
          multiLine={multiLine}
          duration={null}
        />
      </Box>
    </DocumentTitle>
  )
}
